/**
 * Inspector helpers for the external IDE bridge (#345).
 * Pure: no RPC, no filesystem paths.
 */
import { composeAutomaticHandoff, parseFilesChanged, type AutomaticHandoff } from '@brunel/shared';
import type { FinishedRun, Worker, WorkerTerminalRow } from './data';
import { EMPTY_INSPECTOR_SELECTION, type InspectorSelection } from './inspector';

export interface IdeAdapterRow {
  readonly org_id: string;
  readonly node_id: string;
  readonly adapter_key: string;
  readonly display_name: string;
  readonly available: boolean;
  readonly reported_at: string;
}

export interface WorkspaceOpenTargetRow {
  readonly org_id: string;
  readonly node_id: string;
  readonly node_name: string;
  readonly project_id: string;
  readonly project_name: string;
  readonly has_folder: boolean;
  readonly node_connected: boolean;
}

export function inspectorSelectionFromWorker(input: {
  readonly workerId: string;
  readonly worker: Worker | undefined;
  readonly terminal: WorkerTerminalRow | undefined;
  readonly result: FinishedRun | undefined;
}): InspectorSelection {
  const row = input.terminal;
  const worker = input.worker;
  const label = row !== undefined
    ? `Worker ${row.worker_number} · ${row.worker_name}`
    : worker !== undefined
      ? `Worker ${worker.number} · ${worker.name}`
      : input.workerId;
  const filesChanged = parseFilesChanged(input.result?.files_changed ?? null);
  const handoff = handoffFromResult(input.result, {
    workerId: input.workerId,
    workerNumber: row?.worker_number ?? worker?.number ?? null,
    workerName: row?.worker_name ?? worker?.name ?? null,
    projectName: row?.project_name ?? null,
    taskId: row?.task_id ?? input.result?.task_id ?? null,
    taskTitle: row?.task_title ?? null,
    filesChanged,
  });
  return {
    ...EMPTY_INSPECTOR_SELECTION,
    workerId: input.workerId,
    workerLabel: label,
    taskId: row?.task_id ?? input.result?.task_id ?? null,
    taskTitle: row?.task_title ?? null,
    nodeId: row?.node_id ?? null,
    nodeName: row?.node_name ?? null,
    projectId: row?.project_id ?? input.result?.project_id ?? null,
    projectName: row?.project_name ?? null,
    sessionId: row?.session_id ?? row?.workforce_session_id ?? null,
    filesChanged,
    patchText: null,
    runSummary: input.result?.summary ?? null,
    handoff,
  };
}

function handoffFromResult(
  result: FinishedRun | undefined,
  extras: {
    readonly workerId: string;
    readonly workerNumber: number | null;
    readonly workerName: string | null;
    readonly projectName: string | null;
    readonly taskId: string | null;
    readonly taskTitle: string | null;
    readonly filesChanged: readonly string[] | null;
  },
): AutomaticHandoff | null {
  if (result == null && extras.taskId == null) return null;
  return composeAutomaticHandoff({
    worker: { id: extras.workerId, number: extras.workerNumber, name: extras.workerName },
    project: extras.projectName,
    task: { id: extras.taskId, title: extras.taskTitle },
    run: result == null ? null : {
      success: result.success,
      summary: result.summary,
      startedAt: result.started_at,
      endedAt: result.reported_at,
    },
    runtime: result?.runtime_key ?? result?.assigned_runtime_key ?? null,
    model: result?.model_key ?? result?.assigned_model_key ?? null,
    filesChanged: extras.filesChanged,
  });
}

export function latestResultForWorker(
  workerId: string,
  taskId: string | null,
  results: readonly FinishedRun[],
): FinishedRun | undefined {
  const owned = results.filter((row) => row.worker_id === workerId);
  if (taskId != null) {
    const match = owned.find((row) => row.task_id === taskId);
    if (match) return match;
  }
  return owned[0];
}
